/**
 * relay-client.js — Brainzyme® Quiz Submit Handler v2.0 (2026-05-04)
 *
 * Async submit to Cloudflare Worker → KV durable capture → Shopify Customer API.
 *
 * v2.0 change: endpoint moved from Coolify Python relay (deprecated) to Cloudflare
 * Worker `/api/quiz-submit`. Worker writes to KV first (durable), then forwards
 * to the matching Shopify store based on payload.market. Failed forwards retry
 * hourly via cron. See https://apps.nutritionalproducts.org/quiz-bridge/
 *
 * Auth: X-Brainzyme-Quiz-Source header (informational only — Worker doesn't gate
 * on it; CORS-locked to Brainzyme storefront origins).
 * Retries on next page load by calling BrainzymeQuizRelay.drainQueue() on init.
 *
 * Exports: window.BrainzymeQuizRelay
 */

'use strict';

(function (global) {

  const DEFAULT_ENDPOINT = 'https://white-flower-17a2-brainzyme-reviews.nutritionalproducts-org-account.workers.dev/api/quiz-submit';
  const TIMEOUT_MS = 5000;
  const QUEUE_KEY  = 'brainzymeQuizQueue';

  /* ─────────────────────────────────────────────
     QUEUE HELPERS
  ───────────────────────────────────────────── */

  function getQueue() {
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (_) {
      return [];
    }
  }

  function addToQueue(payload) {
    try {
      const queue = getQueue();
      queue.push({
        payload,
        queuedAt: new Date().toISOString(),
        attempts: 0,
      });
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
      console.log('[BzRelay] Queued to localStorage. Queue length:', queue.length);
    } catch (_) {
      console.warn('[BzRelay] localStorage unavailable — payload lost.');
    }
  }

  function clearQueue() {
    try {
      localStorage.removeItem(QUEUE_KEY);
    } catch (_) {}
  }

  function updateQueue(queue) {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    } catch (_) {}
  }

  /* ─────────────────────────────────────────────
     FETCH WITH TIMEOUT
  ───────────────────────────────────────────── */

  async function fetchWithTimeout(url, options, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const resp = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timer);
      return resp;
    } catch (err) {
      clearTimeout(timer);
      throw err;
    }
  }

  /* ─────────────────────────────────────────────
     CORE SUBMIT FUNCTION
  ───────────────────────────────────────────── */

  /**
   * Submit a quiz lead payload to the relay.
   *
   * @param {object} payload - Lead data (see spec §7 ghl_webhook.payload_fields)
   * @param {object} [options]
   * @param {string} [options.endpoint] - Override default relay URL
   * @param {string} [options.source]   - Auth header value (e.g. "A-uk")
   * @returns {Promise<{ok: boolean, forwardStatus: string|null, customerId: number|null, httpStatus: number|null, queued: boolean}>}
   *   `ok` = true only when Worker accepted submission AND forwarded to Shopify
   *   (forwardStatus === 'forwarded'). `queued` = true when payload was added
   *   to localStorage queue for later retry.
   */
  async function submitToRelay(payload, options = {}) {
    const endpoint = options.endpoint || DEFAULT_ENDPOINT;
    const source   = options.source   || 'unknown';

    // Always log payload to console during preview phase
    console.log('[BzRelay] Submitting payload:', JSON.stringify(payload, null, 2));

    try {
      const resp = await fetchWithTimeout(endpoint, {
        method:  'POST',
        headers: {
          'Content-Type':              'application/json',
          'X-Brainzyme-Quiz-Source':   source,
        },
        body: JSON.stringify(payload),
      }, TIMEOUT_MS);

      if (resp.ok) {
        let body = null;
        try { body = await resp.json(); } catch (_) {}
        // Worker contract is snake_case; accept camelCase too in case a future
        // alt-relay or proxy returns the renamed field.
        const forwardStatus = body && (body.forward_status || body.forwardStatus) || null;
        const customerId    = body && (body.shopify_customer_id || body.shopifyCustomerId) || null;

        if (forwardStatus === 'forwarded') {
          console.log('[BzRelay] Success — forwarded to Shopify, customer:', customerId);
          drainQueue({ endpoint, source });
          return { ok: true, forwardStatus, customerId, httpStatus: resp.status, queued: false };
        }

        // HTTP 200 but forward not OK (forward_dead, forward_failed, or missing field).
        // KV write succeeded so payload is durably captured; Shopify forward did NOT.
        // Do NOT re-queue (server already has the durable record).
        console.warn('[BzRelay] HTTP', resp.status, 'but forward_status=', forwardStatus, '— durable on server but not in Shopify yet.');
        return { ok: false, forwardStatus, customerId: null, httpStatus: resp.status, queued: false };
      } else {
        const body = await resp.text().catch(() => '');
        console.warn('[BzRelay] Non-OK response:', resp.status, body);
        addToQueue(payload);
        return { ok: false, forwardStatus: null, customerId: null, httpStatus: resp.status, queued: true };
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.warn('[BzRelay] Timeout after', TIMEOUT_MS, 'ms');
      } else {
        console.warn('[BzRelay] Network error:', err.message);
      }

      // Show user a soft error — not a silent fail
      _dispatchRelayEvent('failure', { payload, error: err.message });
      addToQueue(payload);
      return { ok: false, forwardStatus: null, customerId: null, httpStatus: null, queued: true };
    }
  }

  /* ─────────────────────────────────────────────
     DRAIN QUEUE (called on page load + after success)
  ───────────────────────────────────────────── */

  async function drainQueue(options = {}) {
    const queue = getQueue();
    if (!queue.length) return;

    const endpoint = options.endpoint || DEFAULT_ENDPOINT;
    const source   = options.source   || 'unknown';

    console.log('[BzRelay] Draining queue:', queue.length, 'items');
    const remaining = [];

    for (const item of queue) {
      try {
        const resp = await fetchWithTimeout(endpoint, {
          method:  'POST',
          headers: {
            'Content-Type':            'application/json',
            'X-Brainzyme-Quiz-Source': source,
          },
          body: JSON.stringify(item.payload),
        }, TIMEOUT_MS);

        if (resp.ok) {
          console.log('[BzRelay] Drained queued item. Queued at:', item.queuedAt);
        } else {
          item.attempts = (item.attempts || 0) + 1;
          // Keep in queue if fewer than 5 attempts
          if (item.attempts < 5) remaining.push(item);
        }
      } catch (_) {
        item.attempts = (item.attempts || 0) + 1;
        if (item.attempts < 5) remaining.push(item);
      }
    }

    if (remaining.length === 0) {
      clearQueue();
    } else {
      updateQueue(remaining);
    }
  }

  /* ─────────────────────────────────────────────
     CUSTOM EVENTS
  ───────────────────────────────────────────── */

  function _dispatchRelayEvent(type, detail) {
    try {
      const event = new CustomEvent('bz-relay:' + type, { detail, bubbles: true });
      document.dispatchEvent(event);
    } catch (_) {}
  }

  /* ─────────────────────────────────────────────
     EXPORT
  ───────────────────────────────────────────── */

  global.BrainzymeQuizRelay = {
    submitToRelay,
    drainQueue,
    getQueue,
    clearQueue,
    DEFAULT_ENDPOINT,
  };

}(window));
